import { useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import classNames from 'classnames';
import Flag from '../Flag/Flag';
import Button from '../Button';
import s from './OrganizationChartV2.module.scss';

export const AVATAR_LIMIT = 3;
export const AVATAR_FALLBACK = '/img/person.svg';

// People without a portrait cannot be shown as an avatar but still count
// towards the overflow badge.
export const avatarOverflow = (relatedIndividuals = []) =>
    relatedIndividuals.length -
    Math.min(relatedIndividuals.filter((i) => i.src).length, AVATAR_LIMIT);

// A portrait that 404s would otherwise show a broken-image glyph, and it also
// makes the chart export reject outright.
const Avatar = ({ src }) => {
    const [failed, setFailed] = useState(false);

    return (
        <img
            src={failed ? AVATAR_FALLBACK : src}
            alt=""
            className={s.Avatar}
            onError={() => setFailed(true)}
        />
    );
};

const formatRevenue = (value) => {
    const n = parseFloat(value);
    if (!n) return null;
    if (n >= 1e9) return `$${Math.round(n / 1e9)}bn revenue`;
    if (n >= 1e6) return `$${Math.round(n / 1e6)}mn revenue`;
    return `$${Math.round(n).toLocaleString('en-US')} revenue`;
};

const formatEmployees = (value) => {
    const n = parseFloat(value);
    if (!n) return null;
    if (n >= 1000) return `${Math.round(n / 1000)}k employees`;
    return `${Math.round(n)} employees`;
};

const OrganizationChartV2Node = ({ data }) => {
    const {
        name,
        countryLabel,
        country_code: countryCode,
        revenue,
        employees,
        incorporated,
        relatedIndividuals = [],
        hasToggle,
        hiddenChildren = 0,
        isExpanded,
        isActive,
        isEntering,
        onToggle,
        companyUrl = '#',
    } = data;

    const meta = [
        formatRevenue(revenue),
        formatEmployees(employees),
        incorporated ? `Est. ${incorporated}` : null,
    ].filter(Boolean);

    const avatars = relatedIndividuals
        .filter((i) => i.src)
        .slice(0, AVATAR_LIMIT)
        .map((i) => (i.src?.use_avatar ? AVATAR_FALLBACK : i.src));
    const overflow = avatarOverflow(relatedIndividuals);

    return (
        <div
            className={classNames(s.Card, {
                [s['Card--Active']]: isActive,
                [s['Card--Entering']]: isEntering,
            })}>
            <Handle
                type="target"
                position={Position.Top}
                className={s.Handle}
            />
            <div className={s.CardHeader}>
                <h3 className={s.CardName}>{name}</h3>
                <span className={s.CardCountry}>
                    <span className={s.CardCountryCode}>
                        {countryLabel || countryCode}
                    </span>
                    <Flag countryCode={countryCode} width="1.33rem" />
                </span>
            </div>
            <div className={s.CardMeta}>
                {(avatars.length > 0 || overflow > 0) && (
                    <span className={s.Avatars}>
                        {avatars.map((src, i) => (
                            <Avatar key={i} src={src} />
                        ))}
                        {overflow > 0 && (
                            <span className={s.AvatarOverflow}>
                                +{overflow}
                            </span>
                        )}
                    </span>
                )}
                <span className={s.MetaText}>
                    {meta.map((item, i) => (
                        <span key={i}>
                            {i > 0 && <span className={s.Dot}>•</span>}
                            {item}
                        </span>
                    ))}
                </span>
            </div>
            <Button
                modifier="Secondary"
                size="Small"
                isFullWidth
                title="Company overview"
                url={companyUrl}
                className={isActive ? s.ActiveButton : undefined}
                onClick={(e) => e.stopPropagation()}
            />
            {hasToggle && (
                <Button
                    modifier="Secondary"
                    size="Small"
                    isFullWidth
                    title={
                        isExpanded ? (
                            'Hide network'
                        ) : (
                            <>
                                Explore network{' '}
                                <span className={s.HiddenCount}>
                                    (+{hiddenChildren})
                                </span>
                            </>
                        )
                    }
                    className={isActive ? s.ActiveButton : undefined}
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggle?.();
                    }}
                />
            )}
            <Handle
                type="source"
                position={Position.Bottom}
                className={s.Handle}
            />
        </div>
    );
};

export default OrganizationChartV2Node;
